import { Close, OpenInNewOutlined } from "@mui/icons-material";
import {
  Alert, Box, Button, CircularProgress, Divider, Drawer, FormControl, IconButton,
  Link, MenuItem, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, TableSortLabel, TextField, Typography
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { CustomerExecutiveBriefingFeedbackDrilldown } from "../../../interfaces/CloseTheLoop.interfaces";
import { useGetExecutiveBriefingFeedbackDrilldownDataQuery } from "../../../slices/CloseTheLoop.slice";
import { THEME } from "../utils/theme";

type FeedbackColumn = Exclude<keyof CustomerExecutiveBriefingFeedbackDrilldown, "feedbackUrl">;
type Props = {
  tenantId: string;
  tenantName: string;
  productName: string;
  feedbackPeriod: string;
  timeframeLabel: string;
  label: string;
  recordIds: string[];
  onClose: () => void;
};

const columns: { key: FeedbackColumn; label: string; width: number }[] = [
  { key: "feedbackId", label: "Record", width: 180 },
  { key: "feedbackTitle", label: "Request", width: 310 },
  { key: "feedbackStatus", label: "Status", width: 165 }
];
const inactiveStatuses = new Set(["closed", "resolved", "rejected", "completed"]);
export const isActiveFeedbackStatus = (status: string) => !inactiveStatuses.has(status.trim().toLowerCase());
const displayValue = (row: CustomerExecutiveBriefingFeedbackDrilldown, key: FeedbackColumn): string => {
  const value = row[key];
  if (!value?.trim()) return "Not available";
  if (key.endsWith("Date")) {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) return date.toLocaleString();
  }
  return value;
};

const ProductSnapshotFeedbackDetails: React.FC<Props> = ({
  tenantId, tenantName, productName, feedbackPeriod, timeframeLabel, label, recordIds, onClose
}) => {
  const query = useGetExecutiveBriefingFeedbackDrilldownDataQuery({ tenantId, productName, feedbackPeriod });
  const [filters, setFilters] = useState<Partial<Record<FeedbackColumn, string>>>({});
  const [recordStatus, setRecordStatus] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortKey, setSortKey] = useState<FeedbackColumn>("feedbackCreatedDate");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const records = useMemo(() => {
    const ids = new Set(recordIds);
    return (query.currentData ?? []).filter((row) => ids.has(row.feedbackId));
  }, [query.currentData, recordIds]);
  const activeCount = records.filter((row) => isActiveFeedbackStatus(row.feedbackStatus ?? "")).length;
  const statuses = Array.from(records.reduce((result, row) => {
    const key = row.feedbackStatus?.trim().toLowerCase() ?? "";
    const existing = result.get(key);
    result.set(key, { label: row.feedbackStatus?.trim() || "Unspecified", count: (existing?.count ?? 0) + 1 });
    return result;
  }, new Map<string, { label: string; count: number }>()));
  const filteredRows = useMemo(() => records.filter((row) => {
    const status = row.feedbackStatus?.trim().toLowerCase() ?? "";
    return (recordStatus === "all" || (recordStatus === "active"
      ? isActiveFeedbackStatus(status) : recordStatus === `status:${status}`)) &&
      columns.every(({ key }) => displayValue(row, key).toLowerCase().includes(filters[key]?.trim().toLowerCase() ?? ""));
  }).sort((left, right) => {
    const comparison = (left[sortKey] ?? "").localeCompare(right[sortKey] ?? "", undefined, {
      numeric: true, sensitivity: "base"
    });
    return sortDirection === "asc" ? comparison : -comparison;
  }), [records, recordStatus, filters, sortKey, sortDirection]);
  const effectivePage = Math.min(page, Math.max(0, Math.ceil(filteredRows.length / rowsPerPage) - 1));
  const visibleRows = filteredRows.slice(effectivePage * rowsPerPage, (effectivePage + 1) * rowsPerPage);
  const loading = query.isLoading || query.isFetching;

  return (
    <Drawer anchor="right" open onClose={onClose} PaperProps={{
      role: "dialog", "aria-modal": true, "aria-labelledby": "snapshot-feedback-title",
      sx: { width: { xs: "100%", sm: "92vw", xl: 1400 }, maxWidth: "100vw", height: "100dvh", overflow: "hidden" }
    }}>
      <Box sx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}>
        <Box sx={{ px: { xs: 2, sm: 3.75 }, py: 3, display: "flex", justifyContent: "space-between", gap: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography id="snapshot-feedback-title" component="h2" sx={{
              fontSize: 22, fontWeight: 800, lineHeight: 1.4,
              color: "#16324F !important", WebkitTextFillColor: "#16324F !important"
            }}>
              {label}
            </Typography>
            <Typography sx={{ mt: 0.75, fontSize: 13, color: "#607894" }}>
              {[tenantName, productName, timeframeLabel].filter(Boolean).join(" / ")}
            </Typography>
          </Box>
          <IconButton aria-label="Close feedback details" onClick={onClose} sx={{
            alignSelf: "flex-start", width: 36, height: 36, border: "1px solid #D6E2F0",
            borderRadius: 0.75, color: "#607D9B", "&:hover": { bgcolor: "#F5F8FC" }
          }}>
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
        <Divider sx={{ borderColor: "#D6E2F0" }} />
        <Box role="note" sx={{
          mx: { xs: 2, sm: 3.75 }, mt: 2.75, mb: 0.25, px: 1.75, py: 1.75,
          bgcolor: "#EBF5FF", borderRadius: 0.75, flexShrink: 0
        }}>
          <Typography sx={{ fontSize: 13, lineHeight: 1.5, color: "#496F92" }}>
            Illustrative drill-down only. This prototype does not query CRM, ADO, or incident systems.
          </Typography>
        </Box>
        {!loading && !query.isError && (
          <Box sx={{ px: { xs: 2, sm: 3.75 }, py: 2.5, display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            <Typography sx={{ fontSize: 13, color: "#49627B" }}>
              {activeCount} active / {records.length} total
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75 }}>
              <Typography id="snapshot-record-status-label" sx={{ fontSize: 13, color: "#607894" }}>
                Record status
              </Typography>
              <FormControl size="small" sx={{ minWidth: 205 }}>
                <Select labelId="snapshot-record-status-label" value={recordStatus}
                  onChange={(event) => {
                    setRecordStatus(event.target.value);
                    setPage(0);
                  }} sx={{
                    fontSize: 13, minHeight: 42, borderRadius: 1, color: "#294B6F",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#CBD9E8" }
                  }}>
                  <MenuItem value="all">All statuses ({records.length})</MenuItem>
                  <MenuItem value="active">Active only ({activeCount})</MenuItem>
                  {statuses.map(([key, status]) => (
                    <MenuItem key={key} value={`status:${key}`}>{status.label} ({status.count})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        )}
        {!loading && !query.isError && records.length < new Set(recordIds).size && (
          <Alert severity="warning" sx={{ mx: { xs: 2, sm: 3.75 }, mb: 1 }}>
            Detailed feedback is available for {records.length} of {new Set(recordIds).size} snapshot records.
          </Alert>
        )}
        <TableContainer sx={{ minHeight: 0, flex: 1, px: { xs: 2, sm: 3.75 }, boxSizing: "border-box" }}>
          <Table stickyHeader size="small" aria-label={`${label} details`} sx={{
            "& .MuiTableCell-root": { border: "1px solid #D2D2D2", fontSize: 13 }
          }}>
            <TableHead>
              <TableRow sx={{
                height: 44,
                "& .MuiTableCell-root, & .MuiTableSortLabel-root, & .MuiTableSortLabel-root:hover, & .MuiTableSortLabel-root.Mui-active": {
                  color: "rgba(0, 0, 0, 0.87) !important",
                  WebkitTextFillColor: "rgba(0, 0, 0, 0.87) !important"
                }
              }}>
                {columns.map(({ key, label: columnLabel, width }) => (
                  <TableCell key={key} sx={{ minWidth: width, top: 0, bgcolor: "#F7F9FB", fontWeight: 800, whiteSpace: "nowrap" }}>
                    <TableSortLabel active={sortKey === key} direction={sortKey === key ? sortDirection : "asc"}
                      sx={{
                        color: "inherit !important", fontSize: "inherit", fontWeight: "inherit",
                        "& .MuiTableSortLabel-icon": { color: "rgba(0, 0, 0, 0.54) !important" }
                      }}
                      onClick={() => {
                        setSortDirection(sortKey === key && sortDirection === "asc" ? "desc" : "asc");
                        setSortKey(key);
                        setPage(0);
                      }}>{key === "feedbackTitle" && label === "IcMs" ? "Incident" : columnLabel}</TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                {columns.map(({ key, label: columnLabel, width }) => (
                  <TableCell key={key} sx={{ minWidth: width, top: 44, px: 0.75, py: 0.6, bgcolor: "#FAFBFC" }}>
                    <TextField fullWidth size="small" placeholder="Filter" value={filters[key] ?? ""}
                      onChange={(event) => {
                        setFilters((current) => ({ ...current, [key]: event.target.value }));
                        setPage(0);
                      }} inputProps={{ "aria-label": `Filter by ${key === "feedbackTitle" && label === "IcMs" ? "Incident" : columnLabel}` }}
                      sx={{ "& .MuiInputBase-input": { px: 1, py: 0.65, fontSize: 12 } }} />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={columns.length} align="center" sx={{ py: 5 }}>
                  <Stack spacing={1} alignItems="center">
                    <CircularProgress size={26} aria-label="Loading feedback details" />
                    <Typography sx={{ fontSize: 13 }}>Loading feedback details...</Typography>
                  </Stack>
                </TableCell></TableRow>
              ) : query.isError ? (
                <TableRow><TableCell colSpan={columns.length} align="center" sx={{ py: 5 }}>
                  <Alert severity="error" action={<Button onClick={() => query.refetch()}>Retry</Button>}>
                    Feedback details could not be loaded.
                  </Alert>
                </TableCell></TableRow>
              ) : visibleRows.length === 0 ? (
                <TableRow><TableCell colSpan={columns.length} align="center" sx={{ py: 5 }}>
                  {records.length ? "No records match the selected filters." : "No feedback details are available for this selection."}
                </TableCell></TableRow>
              ) : visibleRows.map((row, index) => (
                <TableRow key={`${row.feedbackId}-${index}`} hover>
                  {columns.map(({ key }) => {
                    const value = displayValue(row, key);
                    return (
                      <TableCell key={key} title={value} sx={{ py: 1.5 }}>
                        {key === "feedbackId" && /^https?:\/\//i.test(row.feedbackUrl) ? (
                          <Link href={row.feedbackUrl} target="_blank" rel="noopener noreferrer"
                            aria-label={`Open ${row.feedbackId} in a new tab`} sx={{ display: "inline-flex", gap: 0.5, alignItems: "center", whiteSpace: "nowrap" }}>
                            {value}<OpenInNewOutlined sx={{ fontSize: 16 }} />
                          </Link>
                        ) : (
                          <Box sx={{
                            maxWidth: key === "feedbackTitle" ? "100%" : key === "feedbackDetails" ? 400 : 310,
                            overflowWrap: key === "feedbackTitle" ? "break-word" : "anywhere",
                            ...(key === "feedbackTitle" ? { wordBreak: "break-word", whiteSpace: "normal" } : {}),
                            ...(key === "feedbackDetails" || key === "feedbackTitle" ? {
                              display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 4, overflow: "hidden"
                            } : {}) }}>{value}</Box>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={loading || query.isError ? 0 : filteredRows.length}
          page={loading || query.isError ? 0 : effectivePage} rowsPerPage={rowsPerPage} rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={(_event, nextPage) => setPage(nextPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }} sx={{ flexShrink: 0, borderTop: `1px solid ${THEME.border}` }} />
      </Box>
    </Drawer>
  );
};

export default ProductSnapshotFeedbackDetails;
